/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The one overlay layout every portaled modal on the public site shares
 * (kWh estimator, MID checker, signup form, the comparer's filter sheet).
 * Class strings plus one thin viewport component, so each modal keeps its own
 * motion wrappers, refs and aria wiring unchanged.
 *
 * Why these particular classes, all of them learned on phones:
 *
 * - `fixed inset-0` + `max-h-full` on the panel, not `max-h-[90vh]`: `vh` is
 *   the LARGE viewport, so with the browser's toolbars showing a 90vh panel
 *   was taller than the screen and its bottom row — the estimator's Apply
 *   button — sat behind Safari's bar. A percentage of the fixed container is
 *   the screen as it actually is.
 * - …and the screen as it actually is excludes the on-screen keyboard, which
 *   `inset-0` alone does not know about: `ModalViewport` pins the fixed
 *   container to the visual viewport while it is mounted (see
 *   src/lib/visualViewport.ts). Without it, everything under the keyboard was
 *   out of reach — the page is locked and the panel's scroll range was
 *   computed for the full screen.
 * - Top-aligned below `sm`, centred from `sm`: a centred card re-centres every
 *   time its content height changes (each wizard step), and the moment the
 *   keyboard opens for the MID typeahead the input is in the lower half of the
 *   screen, exactly where the keyboard goes. Pinned to the top the input stays
 *   visible and the card stops jumping between steps. Desktop is unchanged.
 * - No `backdrop-filter` below `md`: a full-screen blur over the page costs a
 *   phone GPU a repaint per frame of the entrance animation and every scroll
 *   inside the panel. A slightly denser plain veil reads the same.
 * - `touch-none` on the backdrop and `overscroll-contain` on the panel: this
 *   is what keeps a touch scroll inside the modal from dragging the page
 *   behind it on iOS, where `body { overflow: hidden }` alone never did.
 */

import React, { useRef } from 'react';
import { useVisualViewportFit } from '../lib/visualViewport';

export const MODAL_VIEWPORT_CLASS =
  'fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-3 sm:p-4';

export const MODAL_BACKDROP_CLASS =
  'absolute inset-0 touch-none bg-slate-950/85 md:bg-slate-950/80 md:backdrop-blur-md';

export const MODAL_PANEL_CLASS =
  'relative w-full max-h-full overflow-y-auto overscroll-contain bg-slate-900 border border-emerald-500/20 rounded-3xl shadow-2xl shadow-emerald-900/30';

/**
 * The sheet variant (the signup wizard): the whole screen below `sm`, the same
 * card as MODAL_PANEL_CLASS from `sm` up. The panel itself does not scroll —
 * its content is a header, a body and a footer, and only the body scrolls
 * (`flex-auto min-h-0 overflow-y-auto` on it), so the step's title and its
 * buttons stay where they are and, with the keyboard up, the buttons sit right
 * on top of it.
 */
export const MODAL_SHEET_VIEWPORT_CLASS =
  'fixed inset-0 z-[200] flex items-stretch sm:items-center justify-center sm:p-4';

export const MODAL_SHEET_PANEL_CLASS =
  'relative flex flex-col w-full max-h-full overflow-hidden bg-slate-900 shadow-2xl shadow-emerald-900/30 sm:border sm:border-emerald-500/20 sm:rounded-3xl';

/**
 * The bottom-sheet variant of the viewport (the comparer's "Alle filters",
 * src/components/comparison/FilterSheet.tsx) — not the wizard's full-screen
 * sheet above: below `sm` the panel rests on the bottom edge, full width and
 * without a margin — where a thumb already is, and where a sheet that grows or
 * shrinks with its content only ever moves its top edge; from `sm` it is the
 * ordinary centred card. Rendered through `ModalViewport` like every other
 * modal, so with the keyboard up the bottom edge is the keyboard's top edge.
 * The panel caps itself at `85svh` (the SMALL viewport, toolbars showing —
 * never `vh`, see above) and at the viewport's own height, so the page stays
 * visible above it, the backdrop above the sheet is always there to tap, and
 * the keyboard shrinks the sheet instead of pushing its top off the screen.
 */
export const FILTER_SHEET_VIEWPORT_CLASS =
  'fixed inset-0 z-[200] flex items-end justify-center p-0 sm:items-center sm:p-4';

interface ModalViewportProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

/**
 * The fixed viewport a modal sits in, kept inside the visible part of the screen.
 * Any other attribute lands on the same element (the filter sheet makes it
 * `inert` and `aria-hidden` while it animates out).
 */
export const ModalViewport: React.FC<ModalViewportProps> = ({
  className = MODAL_VIEWPORT_CLASS,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useVisualViewportFit(ref);
  return (
    <div ref={ref} className={className} {...rest}>
      {children}
    </div>
  );
};
